import { AuthenticationUser, AuthRoleEnum } from '@codezo/graphql';
import { Route, Routes } from 'react-router-dom';
import { AuthCallbackPage } from './AuthCallbackPage';
import { LogoutCallback } from './LogoutCallback';
import { ReAuthenticateCallback } from './ReAuthenticateCallback';

type AuthRoutesProps<TUser> = {
	setUser: (user: TUser | null) => void;
	setAccessToken: (token: string | null) => void;
	userFormatter: (user: AuthenticationUser) => TUser;
	token: string | null;
	requiredRole: AuthRoleEnum;
	/**
	 * Recovery path for sessions the standard login page can't restore (see
	 * ReAuthenticateCallback). Return `null` for the default behaviour.
	 */
	resolveFailureRedirect?: () => string | null;
	/**
	 * Destination the user was heading for before signing in (see
	 * AuthCallbackPage). Return `null` for the default landing page.
	 */
	resolvePendingRedirect?: () => string | null;
};

export const AuthRoutes = <TUser,>({
	setUser,
	setAccessToken,
	userFormatter,
	token,
	requiredRole,
	resolveFailureRedirect,
	resolvePendingRedirect,
}: AuthRoutesProps<TUser>) => {
	return (
		<Routes>
			<Route
				path="/auth/callback"
				element={
					<AuthCallbackPage
						requiredRole={requiredRole}
						successRedirect="/dashboard"
						resolvePendingRedirect={resolvePendingRedirect}
						setUser={(user) => setUser(userFormatter(user))}
						setAccessToken={setAccessToken}
					/>
				}
			/>
			<Route
				path="/auth/reauth"
				element={
					<ReAuthenticateCallback
						setUser={(user) => setUser(userFormatter(user))}
						setAccessToken={setAccessToken}
						resolveFailureRedirect={resolveFailureRedirect}
					/>
				}
			/>
			<Route
				path="/auth/logout"
				element={
					<LogoutCallback
						setUser={() => setUser(null)}
						setAccessToken={setAccessToken}
						token={token}
					/>
				}
			/>
		</Routes>
	);
};
